import { useCallback, useEffect, useState } from 'react'
import {
  getCallBoostEnabled, getCallBoostGain,
  setCallBoostEnabled, setCallBoostGain,
} from './callAudioBoostPrefs'

// Cross-component sync for live-call boost prefs. The state lives in
// localStorage, but multiple components (sidebar settings panel, mobile
// settings modal, in-call popover, and the active useCallAudioBoost
// instance inside usePhoneCall) all need to react when ANY of them
// mutates it — including while a call is in progress. localStorage's
// `storage` event only fires across tabs, so we dispatch an in-tab
// CustomEvent on every write and have all hook instances subscribe.
//
// In-tab CustomEvent carries the new value as `detail` rather than
// triggering listeners to re-read localStorage. This matters in
// private-mode browsers where `localStorage.setItem` silently fails:
// re-reading would return the default and immediately revert the
// in-memory state we just set, making the toggle physically reject
// user input. With `detail`-based delivery the in-memory state stays
// authoritative for the session even if persistence didn't take.
//
// Cross-tab `storage` events still re-read both keys — there we
// genuinely don't know what changed, and the other tab's localStorage
// is the source of truth.

const BOOST_CHANGED_EVENT = 'pixellab.callboost.changed'

type BoostChangeDetail =
  | { kind: 'enabled'; value: boolean }
  | { kind: 'gain'; value: number }

/**
 * Hook returning the current boost state and stable setters. Multiple
 * instances stay in sync — when any instance calls a setter, every
 * other instance re-renders with the new value.
 */
export function useCallBoost(): {
  enabled: boolean
  gain: number
  setEnabled: (v: boolean) => void
  setGain: (v: number) => void
} {
  const [enabled, setEnabledState] = useState<boolean>(() => getCallBoostEnabled())
  const [gain, setGainState] = useState<number>(() => getCallBoostGain())

  useEffect(() => {
    const onCustom = (e: Event): void => {
      // In-tab CustomEvent carries the new value as detail. Apply it
      // directly without re-reading localStorage so a silent write
      // failure (private mode / quota) doesn't revert the setter.
      const detail = (e as CustomEvent<BoostChangeDetail>).detail
      if (!detail) return
      if (detail.kind === 'enabled') setEnabledState(detail.value)
      else setGainState(detail.value)
    }
    const onStorage = (): void => {
      // Cross-tab: another tab mutated localStorage. Re-read both keys
      // since we don't know what specifically changed.
      setEnabledState(getCallBoostEnabled())
      setGainState(getCallBoostGain())
    }
    window.addEventListener(BOOST_CHANGED_EVENT, onCustom)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(BOOST_CHANGED_EVENT, onCustom)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const setEnabled = useCallback((v: boolean): void => {
    setCallBoostEnabled(v)
    setEnabledState(v)
    window.dispatchEvent(new CustomEvent<BoostChangeDetail>(BOOST_CHANGED_EVENT, {
      detail: { kind: 'enabled', value: v },
    }))
  }, [])

  const setGain = useCallback((v: number): void => {
    setCallBoostGain(v)
    setGainState(v)
    window.dispatchEvent(new CustomEvent<BoostChangeDetail>(BOOST_CHANGED_EVENT, {
      detail: { kind: 'gain', value: v },
    }))
  }, [])

  return { enabled, gain, setEnabled, setGain }
}
